// Pure Company[] -> CSV text for the admin vendor-list export. React-free and
// unit-tested (same pattern as pagination.ts) — the app has no DOM test env.
// Column order is explicit (not Object.keys) so the export is stable.
import type { Company } from '@l2u/ui/lib/types.ts';

// Every admin-present Company field, in a fixed, human-sensible order. (The
// vendor-only `has_submitted_before` is intentionally excluded — it is absent
// on admin payloads and would always be blank.) The `as const satisfies
// readonly (keyof Company)[]` below guarantees every key is a real Company field.
export const VENDOR_CSV_COLUMNS = [
  'id',
  'company_name',
  'vendor_number',
  'contact_name',
  'email',
  'contractor_types',
  'contract_numbers',
  'cluster_region',
  'cluster_regions',
  'station',
  'stations',
  'territory',
  'status',
  'status_before_deactivation',
  'progress',
  'abn',
  'abn_verification_status',
  'abn_entity_name',
  'abn_registry_status',
  'abn_verified_at',
  'legal_entity_name',
  'trustee_info',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
  'employee_count',
  'address_line',
  'city',
  'state',
  'postcode',
  'country',
  'invited_at',
  'completed_at',
  'expires_at',
  'created_at',
  'updated_at',
  'auth_user_id',
] as const satisfies readonly (keyof Company)[];

/** RFC 4180 cell encoder. Exported so other CSV builders (expiringCsv.ts)
 *  escape identically rather than reimplementing the rules. */
export function csvCell(value: unknown): string {
  if (value == null) return '';
  const raw = Array.isArray(value) ? value.join('; ') : String(value);
  // RFC 4180: quote when the value contains a comma, quote, CR or LF; escape
  // embedded quotes by doubling them.
  if (/[",\r\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
  return raw;
}

export function buildVendorCsv(companies: Company[]): string {
  const header = VENDOR_CSV_COLUMNS.join(',');
  const rows = companies.map((c) => VENDOR_CSV_COLUMNS.map((col) => csvCell(c[col])).join(','));
  return [header, ...rows].join('\r\n');
}
